// Service content for HIG Automation.
// Edit this file to change service titles, text, images, benefits, applications,
// detailed sections and SEO. React page components will read from this file.

export const coreServices = [
  {
    slug: "plc-scada-automation",
    title: "PLC & SCADA Automation",
    cardImage: "/images/services/plc-&-automation-panels.webp",
    tagline: "Smart control, visualization and data-driven automation for modern factories.",
    eyebrow: "Industrial Control & Digitalization",
    description:
      "HIG Automation delivers PLC and SCADA solutions that connect machines, processes and operators through reliable industrial control architecture. We engineer new systems, modernize legacy controls and integrate visualization, networking and production data so manufacturing teams can run equipment with better visibility and less downtime.",
    heroImage:
      "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1800&q=85",
    image:
      "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1200&q=80",
    seo: {
      title: "PLC & SCADA Automation Services India | HIG Automation",
      description:
        "PLC programming, HMI and SCADA development, industrial networking, remote monitoring and automation retrofit services from HIG Automation India.",
    },
    benefits: [
      "Improved machine availability and production visibility",
      "Faster fault diagnosis and structured alarm handling",
      "Scalable controls prepared for future line expansion",
      "Remote data access and production reporting options",
    ],
    useCases: [
      "Machine and line automation",
      "Legacy PLC migration and retrofit",
      "Utility and process monitoring",
      "Production data logging and dashboards",
    ],
    bullets: [
      "PLC Programming — Siemens, ABB, Delta and Mitsubishi",
      "SCADA Development & HMI Design",
      "Industrial Control Panel Integration",
      "Industrial Ethernet & Fieldbus Networking",
      "Remote Monitoring & Data Logging",
    ],
    sections: [
      {
        title: "PLC Programming & Control Engineering",
        description:
          "Control logic is developed around the actual machine sequence, interlocks, safety requirements and maintenance needs. We support new projects, machine upgrades and legacy migration.",
        image:
          "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?auto=format&fit=crop&w=1200&q=80",
        items: ["Sequence control", "Interlocks and diagnostics", "Retrofit and migration", "Motion and drive integration"],
      },
      {
        title: "SCADA, HMI & Visualization",
        description:
          "Operator interfaces are designed for clear operation, alarm visibility and faster troubleshooting. SCADA systems can consolidate multiple machines or utilities into one monitoring layer.",
        image:
          "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
        items: ["HMI screen development", "Alarm management", "Trend and recipe screens", "Production dashboards"],
      },
      {
        title: "Industrial Networks & Remote Monitoring",
        description:
          "We integrate industrial devices and control systems using practical, maintainable network architectures for real-time data exchange and remote support.",
        image:
          "/images/services/industrial-networks-&-remote-monitoring.webp",
        items: ["PROFINET and Ethernet integration", "Remote I/O integration", "Data logging", "Remote diagnostics"],
      },
    ],
  },
  {
    slug: "paint-shop-automation",
    title: "Paint Shop Automation",
    cardImage: "/images/services/booth,-oven-&-conveyor-integration.webp",
    tagline: "Integrated automation for paint lines, pretreatment and finishing processes.",
    eyebrow: "Paint Line Engineering",
    description:
      "HIG Automation provides integrated automation and engineering support for industrial paint shops. Our scope can cover material movement, process sequencing, booth and oven controls, pretreatment stages, robotic interfaces and production monitoring. We focus on repeatable process control, safer operation and stable line performance.",
    heroImage:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1800&q=85",
    image:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1200&q=80",
    seo: {
      title: "Paint Shop Automation & Paint Line Solutions | HIG Automation",
      description:
        "Paint shop automation for pretreatment, paint booths, ovens, conveyors, robotic painting and paint line modernization for industrial manufacturing.",
    },
    benefits: [
      "More consistent process timing and finishing quality",
      "Reduced manual handling between paint processes",
      "Better monitoring of line status and equipment faults",
      "Modernization of existing paint lines with minimum disruption",
    ],
    useCases: [
      "Automotive component paint lines",
      "Powder coating lines",
      "Industrial wet paint systems",
      "Brownfield paint shop modernization",
    ],
    bullets: [
      "Automatic Paint Booth Controls",
      "Paint Conveyor Systems",
      "Robot Paint Integration",
      "Pretreatment System Automation",
      "Paint Line Modernization",
    ],
    sections: [
      {
        title: "Pretreatment & Process Automation",
        description:
          "We automate process sequences, pumps, valves, dosing interfaces and utility equipment around pretreatment stages to improve repeatability and operational visibility.",
        image:
          "/images/services/pretreatment- process-automation.webp",
        items: ["Stage sequencing", "Pump and valve control", "Tank monitoring", "Alarm and interlock logic"],
      },
      {
        title: "Booth, Oven & Conveyor Integration",
        description:
          "Paint booth equipment, ovens and conveyors are coordinated as one line so product flow and process timing remain synchronized.",
        image:
          "/images/services/booth,-oven-&-conveyor-integration.webp",
        items: ["Booth controls", "Oven sequencing", "Conveyor tracking", "Line status visualization"],
      },
      {
        title: "Robot & Production Monitoring",
        description:
          "Robot interfaces, process status and production data can be integrated into the control system for clearer operation and faster fault response.",
        image:
          "/images/services/robot-&-production.webp",
        items: ["Robot handshaking", "Fault monitoring", "Cycle tracking", "Production data"],
      },
    ],
  },
  {
    slug: "special-purpose-machines",
    title: "Special Purpose Machines",
    cardImage: "/images/services/assembly-&-process-machines.webp",
    tagline: "Custom-built machines engineered around your product and production process.",
    eyebrow: "Custom Machine Engineering",
    description:
      "HIG Automation designs and builds Special Purpose Machines for processes where standard equipment cannot meet the required cycle time, quality or handling method. Our team converts the production requirement into a practical machine concept, develops mechanical and control systems, manufactures the equipment and supports installation and commissioning.",
    heroImage:
      "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&w=1800&q=85",
    image:
      "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&w=1200&q=80",
    seo: {
      title: "Special Purpose Machine Manufacturer India | HIG Automation",
      description:
        "Custom SPM design and manufacturing for assembly, testing, inspection, pressing, material handling and production line automation.",
    },
    benefits: [
      "Machine concept built around your exact process",
      "Reduced dependence on repetitive manual operations",
      "Integrated inspection and poka-yoke possibilities",
      "Single team for mechanical, electrical and automation work",
    ],
    useCases: [
      "Automated assembly",
      "Leak and functional testing",
      "Pressing and joining",
      "Inspection and traceability",
    ],
    bullets: [
      "Assembly Automation",
      "Inspection Machines",
      "Testing Equipment",
      "Custom Machine Design",
      "Production Line Automation",
    ],
    sections: [
      {
        title: "Assembly & Process Machines",
        description:
          "Custom machines combine fixtures, actuators, tooling and sequence control to execute repeatable production operations.",
        image:
          "/images/services/assembly-&-process-machines.webp",
        items: ["Assembly stations", "Pressing systems", "Joining processes", "Automatic fixtures"],
      },
      {
        title: "Testing & Inspection Equipment",
        description:
          "We develop test and inspection machines that combine sensors, data acquisition and pass/fail logic for controlled quality checks.",
        image:
          "/images/services/testing-&-inspection-equipment.webp",
        items: ["Leak testing", "Functional testing", "Vision inspection", "Result logging"],
      },
      {
        title: "Material Handling & Poka-Yoke",
        description:
          "Part presentation, transfer and error-proofing are engineered into the machine to improve operator ergonomics and process reliability.",
        image:
          "/images/services/material-handling-&-poka-yoke.webp",
        items: ["Pick and place", "Part orientation", "Poka-yoke", "Infeed and outfeed handling"],
      },
    ],
  },
  {
    slug: "conveyor-systems",
    title: "Conveyor Systems",
    cardImage: "/images/services/belt-conveyor.webp",
    tagline: "Custom conveyor systems for efficient, controlled material flow.",
    eyebrow: "Conveyor Solutions",
    description:
      "HIG Automation designs, manufactures, installs and commissions custom conveyor systems for automotive, electronics, FMCG, pharmaceutical and heavy engineering industries. Our solutions are engineered to improve productivity, reduce manual handling and optimize material flow across your manufacturing facility. From concept and design to fabrication, automation, installation and after-sales support, we deliver complete turnkey conveyor solutions tailored to your production requirements.",
    heroImage:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1800&q=85",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80",
    seo: {
      title: "Industrial Conveyor Systems Manufacturer India | HIG Automation",
      description:
        "Custom belt, slat, mesh, overhead and floor monorail conveyor systems designed, manufactured and commissioned by HIG Automation.",
    },
    benefits: [
      "Improved material flow and lower manual handling",
      "Custom design for product, load and process layout",
      "PLC, sensor and line-control integration",
      "Turnkey manufacturing, installation and commissioning",
    ],
    useCases: [
      "Assembly and production lines",
      "Paint shop material movement",
      "Heavy component transfer",
      "Packaging and inspection lines",
    ],
    bullets: [
      "Belt Conveyors",
      "Slat Conveyors",
      "Mesh Conveyors",
      "Overhead Conveyors",
      "Floor Monorail Conveyors",
    ],
    sections: [
      {
        title: "Belt Conveyor",
        description:
          "Versatile conveyor systems for continuous transfer of cartons, components and packaged material across assembly and handling processes.",
        image:
          "/images/services/belt-conveyor.webp",
        items: ["Assembly lines", "Packaging lines", "Material handling", "Warehouses", "Inspection stations"],
      },
      {
        title: "Slat Conveyor",
        description:
          "Robust slat-based conveyors for heavier products, fixtures and production processes that need a stable carrying surface.",
        image:
          "/images/services/slat-conveyor.webp",
        items: ["Automotive assembly", "Heavy component transfer", "Engine assembly", "Production lines", "Manufacturing plants"],
      },
      {
        title: "Mesh Conveyor",
        description:
          "Open-mesh conveyor solutions suited to processes that require airflow, drainage or thermal exposure during transfer.",
        image:
          "/images/services/mesh-conveyor.webp",
        items: ["Drying systems", "Heat treatment", "Washing systems", "Powder coating", "Industrial processing"],
      },
      {
        title: "Overhead Conveyor",
        description:
          "Space-efficient overhead material movement for hanging parts and continuous process lines where floor space must remain clear.",
        image:
          "/images/services/overhead-conveyor.jpeg",
        items: ["Paint shops", "Powder coating", "Hanging components", "Automotive body shops", "Continuous production"],
      },
      {
        title: "Floor Monorail Conveyor",
        description:
          "Guided floor conveyor systems for controlled movement of heavy fixtures and products through complex manufacturing layouts.",
        image:
          "/images/services/floor-monorail-conveyor.jpeg",
        items: ["Automotive plants", "Heavy load transfer", "Flexible routing", "Robotic cells", "Engine and chassis assembly"],
      },
    ],
  },
  {
    slug: "fire-protection-systems",
    title: "Fire Protection Systems",
    cardImage: "/images/services/fire-hydrant-networks.webp",
    tagline: "Integrated fire protection infrastructure for industrial facilities.",
    eyebrow: "Industrial Fire Protection",
    description:
      "HIG Automation executes industrial fire protection projects covering hydrant networks, sprinkler systems, pump rooms and associated monitoring and control interfaces. We coordinate site execution, piping, equipment installation and testing with a focus on dependable operation and maintainable plant infrastructure.",
    heroImage:
      "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1800&q=85",
    image:
      "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1200&q=80",
    seo: {
      title: "Industrial Fire Protection Systems | HIG Automation India",
      description:
        "Industrial fire hydrant, sprinkler, pump room and fire protection system installation and maintenance services from HIG Automation.",
    },
    benefits: [
      "Coordinated mechanical and electrical execution",
      "Structured piping and equipment installation",
      "Testing, commissioning and maintenance support",
      "Suitable for new facilities and system upgrades",
    ],
    useCases: [
      "Factories and warehouses",
      "Production buildings",
      "Utility and service areas",
      "Fire protection system upgrades",
    ],
    bullets: [
      "Fire Hydrant Systems",
      "Sprinkler Systems",
      "Fire Alarm Interfaces",
      "Pump Rooms",
      "Annual Maintenance",
    ],
    sections: [
      {
        title: "Fire Hydrant Networks",
        description:
          "Industrial hydrant piping, valves and field equipment are installed with practical routing and service access in mind.",
        image:
          "/images/services/fire-hydrant-networks.webp",
        items: ["Hydrant piping", "Hose stations", "Valves and accessories", "Site installation"],
      },
      {
        title: "Sprinkler & Distribution Systems",
        description:
          "We support sprinkler piping and distribution work for industrial areas as part of integrated fire protection execution.",
        image:
          "/images/services/sprinkler-&-distribution-systems.webp",
        items: ["Distribution piping", "Sprinkler installation", "Zone coordination", "Testing support"],
      },
      {
        title: "Pump Rooms & System Support",
        description:
          "Pump room equipment, piping and control interfaces are brought together for reliable operation and easier maintenance.",
        image:
          "/images/services/pump-rooms-&-system-support.webp",
        items: ["Pump installation", "Header piping", "Control interfaces", "Maintenance support"],
      },
    ],
  },
  {
    slug: "ahu-utility-installations",
    title: "AHU & Utility Installations",
    cardImage: "/images/services/ahu-&-ventilation-installation.jpeg",
    tagline: "AHU, HVAC and industrial utility installation for reliable plant infrastructure.",
    eyebrow: "Plant Utilities & HVAC",
    description:
      "HIG Automation supports industrial facilities with AHU, ventilation and utility installation services. Our teams execute equipment installation, compressed-air piping and utility distribution with attention to routing, accessibility, workmanship and coordination with ongoing production or new-facility schedules.",
    heroImage:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1800&q=85",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80",
    seo: {
      title: "AHU, HVAC & Industrial Utility Installation | HIG Automation",
      description:
        "AHU installation, HVAC support, compressed air piping and industrial utility line installation for manufacturing facilities.",
    },
    benefits: [
      "Single-site coordination for multiple utility activities",
      "Practical routing for operation and maintenance access",
      "Support for greenfield and brownfield factories",
      "Installation, modification and maintenance capability",
    ],
    useCases: [
      "Clean manufacturing areas",
      "Plant compressed-air networks",
      "HVAC and ventilation projects",
      "Utility line expansion and relocation",
    ],
    bullets: [
      "AHU Installation",
      "Compressed Air Piping",
      "HVAC & Ventilation Support",
      "Industrial Utility Lines",
      "Maintenance & Modification",
    ],
    sections: [
      {
        title: "AHU & Ventilation Installation",
        description:
          "Equipment positioning, support structures and associated installation activities are coordinated with the plant layout and service requirements.",
        image:
          "/images/services/ahu-&-ventilation-installation.jpeg",
        items: ["AHU installation", "Ventilation support", "Equipment supports", "Site coordination"],
      },
      {
        title: "Compressed Air Piping",
        description:
          "Plant air networks are routed and installed to support production equipment with clear distribution and maintainable branch connections.",
        image:
          "/images/services/compressed-air-piping.jpg",
        items: ["Main headers", "Machine drops", "Piping modification", "Leak and efficiency support"],
      },
      {
        title: "Industrial Utility Lines",
        description:
          "We execute utility distribution and modification work for manufacturing facilities, including coordinated piping and support structures.",
        image:
          "/images/services/industrial-utility-lines.webp",
        items: ["Utility routing", "Pipe supports", "Line modification", "Plant expansion support"],
      },
    ],
  },
  {
    slug: "control-panels",
    title: "Control Panels",
    cardImage: "/images/services/mcc,-pcc-&-apfc-panels.jpg",
    tagline: "Engineered PLC, MCC, PCC and VFD panels for industrial control systems.",
    eyebrow: "Electrical Control Engineering",
    description:
      "HIG Automation designs, manufactures, wires and commissions industrial control panels for machines, lines and plant systems. Panel layouts are developed around the control architecture, connected loads, serviceability and project standards. We support PLC panels, motor-control panels and drive panels as part of complete automation projects or standalone requirements.",
    heroImage:
      "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1800&q=85",
    image:
      "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1200&q=80",
    seo: {
      title: "Industrial Control Panel Manufacturer | HIG Automation India",
      description:
        "PLC, MCC, PCC, APFC and VFD control panel design, manufacturing, wiring, testing and commissioning for industrial automation.",
    },
    benefits: [
      "Panel design aligned with the complete control system",
      "Clean wiring and device identification for maintenance",
      "Factory testing before dispatch",
      "Integration with PLC, HMI, drives and field devices",
    ],
    useCases: [
      "Machine control panels",
      "Conveyor and line controls",
      "Motor control centers",
      "Drive and process panels",
    ],
    bullets: [
      "PLC Panels",
      "MCC Panels",
      "PCC & APFC Panels",
      "VFD Panels",
      "Electrical Wiring & Testing",
    ],
    sections: [
      {
        title: "PLC & Automation Panels",
        description:
          "PLC, remote I/O, networking and machine-control devices are integrated into organized panels designed for commissioning and maintenance.",
        image:
          "/images/services/plc-&-automation-panels.webp",
        items: ["PLC panels", "Remote I/O panels", "HMI integration", "Industrial networking"],
      },
      {
        title: "MCC, PCC & APFC Panels",
        description:
          "Power distribution and motor-control requirements are engineered around connected loads, operating sequence and plant requirements.",
        image:
          "/images/services/mcc,-pcc-&-apfc-panels.jpg",
        items: ["MCC panels", "PCC panels", "APFC panels", "Motor feeders"],
      },
      {
        title: "VFD & Drive Panels",
        description:
          "Variable-speed drive panels are configured for controlled motor operation and integration with the plant or machine automation system.",
        image:
          "/images/services/vfd-&-drive-panels.webp",
        items: ["VFD panels", "Drive integration", "Bypass requirements", "PLC communication"],
      },
    ],
  },
  {
    slug: "industrial-fabrication",
    title: "Industrial Fabrication",
    cardImage: "/images/services/Machine Frames & Structures.webp",
    tagline: "Precision MS and SS fabrication for machines, plants and industrial projects.",
    eyebrow: "Precision Fabrication",
    description:
      "HIG Automation provides custom industrial fabrication for machine builders, automation projects and manufacturing facilities. We fabricate machine frames, platforms, guards, conveyor structures, piping supports and custom assemblies in mild steel and stainless steel. Our engineering and fabrication teams work together so the finished structure fits the actual equipment and site requirement.",
    heroImage:
      "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=1800&q=85",
    image:
      "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=1200&q=80",
    seo: {
      title: "Industrial Fabrication Services MS & SS | HIG Automation",
      description:
        "Custom MS and SS fabrication for machine frames, platforms, guards, conveyor structures, piping supports and industrial assemblies.",
    },
    benefits: [
      "Engineering and fabrication coordinated by one team",
      "Custom builds from drawings or site requirements",
      "MS and SS fabrication capability",
      "Suitable for one-off and small-batch industrial work",
    ],
    useCases: [
      "Machine and SPM frames",
      "Platforms and walkways",
      "Conveyor support structures",
      "Industrial guards and custom assemblies",
    ],
    bullets: [
      "MS & SS Fabrication",
      "Machine Structures",
      "Platforms & Walkways",
      "Industrial Piping & Supports",
      "Custom Engineering",
    ],
    sections: [
      {
        title: "Machine Frames & Structures",
        description:
          "Frames and structures are fabricated around equipment dimensions, load requirements and integration points.",
        image:
          "/images/services/Machine Frames & Structures.webp",
        items: ["Machine frames", "SPM structures", "Conveyor structures", "Equipment bases"],
      },
      {
        title: "Platforms, Guards & Walkways",
        description:
          "Industrial access and guarding structures are built to suit the equipment layout and operating environment.",
        image:
          "/images/services/platforms,-guards-&-walkways.webp",
        items: ["Platforms", "Walkways", "Machine guards", "Handrails and supports"],
      },
      {
        title: "Custom MS & SS Assemblies",
        description:
          "We support custom engineered assemblies, brackets, enclosures and production aids from customer drawings or jointly developed requirements.",
        image:
          "/images/services/custom-ms-&-ss-assemblies.webp",
        items: ["MS assemblies", "SS assemblies", "Brackets and supports", "Custom industrial fabrication"],
      },
    ],
  },
];
